import { Component, OnInit } from '@angular/core';
import { CreateImageRequest, CreateImageWithRepositoryRequest, ImageDetailedInfoResponse, ImageFormData, ImageWithRepositoryFormData, LocalImageInfo, LocalImageInfoDTO } from 'src/app/core/models/repository';
import { ConfigEntityService } from 'src/app/core/services/config-entity.service';
import { BuildImageComponent } from '../modal/build-image/build-image.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { interval, Subscription } from 'rxjs';
import { SyncConfigService } from 'src/app/core/services/sync-config.service';

@Component({
  selector: 'app-image-management',
  templateUrl: './image-management.component.html',
  styleUrl: './image-management.component.scss'
})
export class ImageManagementComponent implements OnInit {
  images: LocalImageInfo[] = [];
  filteredImages: LocalImageInfo[] = [];
  imagesDTO: LocalImageInfoDTO[] = [];
  filteredImagesDTO: LocalImageInfoDTO[] = [];
  searchTerm = '';
  showSimpleForm = false;
  showRepositoryForm = false;
  isLoading = false;
  isBuildingImage = false;
  isBuildingRepoImage = false;
  inspectingImage = '';
  untaggingImage = '';
  selectedImageDetails: ImageDetailedInfoResponse | null = null;
  successMessage = '';
  config = {
    itemsPerPage: 10,
    currentPage: 1,
  };
  nameSort: boolean = false;
  activeSort: string = "name";
  imageListAutoReloadSub: Subscription;

  constructor(
    private configEntityService: ConfigEntityService,
    public snackBar: MatSnackBar,
    public dialog: MatDialog,
    private spinner: NgxSpinnerService,
    private navService: NavigationService,
    private authenticationService: AuthenticationService,
    private syncConfig: SyncConfigService,
  ) {}

  ngOnInit() {
    this.loadImages();
    this.imageListAutoReloadSub = interval(
          this.syncConfig.config.jobs.detailsReqInterval
        ).subscribe((x) => {
          this.loadImages(false);
        });
  }

  loadImages(showSpinner: boolean = true) {
    try{
      if (showSpinner) {
        this.spinner.show();
      }
      this.isLoading = true;
      this.configEntityService.listLocalImages().subscribe({
        next: (data) => {
          this.images = data;
          this.filteredImages = [...data];
          this.isLoading = false;

          if(this.images && this.images.length > 0){
            this.imagesDTO = this.images.map(img => ({
              ...img,
              imageName: img.repository.includes('/') ? img.repository.split('/').pop() : img.repository,
              registryName: img.repository.includes('/') ? img.repository.split('/')[0] : 'NA',
            }));
            this.filteredImagesDTO = [...this.imagesDTO];
          }
          
          this.spinner.hide();
          switch (this.activeSort) {
            case 'phase':
              // this.sortPhase(false);
              break;
            case 'name':
              this.sortByImageName(showSpinner);
              break;
          }
        },
        error: (error) => {
          this.spinner.hide();
          this.isLoading = false;
          console.error('Error loading images:', error);
        }
      });
    } catch (error) {
      console.error('Error initializing image management:', error);
      this.spinner.hide();
      this.isLoading = false;
      return;
    }
  }

  createImage() {
    try{
      const dialogRef = this.dialog.open(BuildImageComponent, {
          width: "50%",
          autoFocus: true,
          disableClose: true,
          hasBackdrop: false,
          panelClass: "job-dialog-containers",
          data: { name: "", updateForm: false, isBuildAndPush: false },
        });
        dialogRef.afterClosed().subscribe((res) => {
          if (res) {
            this.loadImages(false);
          }
      });
    }
    catch (error) {
      console.error('Error creating image:', error);
      return;
    }
  }


  inspectImage(image: LocalImageInfo) {
    this.inspectingImage = image.id;
    const imageName = `${image.repository}:${image.tag}`;
    
    this.configEntityService.getImageInfo(imageName).subscribe({
      next: (details) => {
        this.selectedImageDetails = details;
        this.inspectingImage = '';
      },
      error: (error) => {
        this.inspectingImage = '';
        console.error('Error inspecting image:', error);
      }
    });
  }

  untagImage(image: LocalImageInfo) {
    try{
      this.untaggingImage = image.id;
      const imageName = `${image.repository}:${image.tag}`;

      const request = {
        force: true,
        image: imageName,
        hostName: "",
      };

      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: "50%",
        autoFocus: false,
        disableClose: false,
        data: {
          titleMessage: 'Delete Image',
          bodyMessage: `Are you sure you want to delete '${imageName}'?`,
          cancelText: "Cancel",
          submitText: "Delete",
        }
      });
      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.configEntityService.untagImage(request).subscribe({
            next: (response) => {
              this.successMessage = JSON.parse(JSON.stringify(response))?.message;
              this.snackBar.open(this.successMessage, 'Close', {
                duration: 5000,
              });
              this.untaggingImage = '';
              this.loadImages();
            },
            error: (error) => {
              this.untaggingImage = '';
              console.error('Error removing image:', error);
            }
          });
        }
      });
    }
    catch (error) {
      console.error('Error untagging image:', error);
    }

  }

  filterImages() {
    this.filteredImages = this.images.filter(image =>
      image.repository.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      image.tag.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      image.id.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  closeImageDetails() {
    this.selectedImageDetails = null;
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getRelativeTime(dateString: string): string {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
    return `${Math.floor(diffInSeconds / 31536000)} years ago`;
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  clearMessages() {
    this.successMessage = '';
  }

  sortByImageName(toggle: boolean) {
    if (toggle) {
      this.nameSort = !this.nameSort;
    }
    this.activeSort = "name";
    this.imagesDTO = this.imagesDTO.sort((a, b) => {
      const aName = a.imageName.toLowerCase();
      const bName = b.imageName.toLowerCase();
      if (this.nameSort) {
        return aName < bName ? -1 : aName > bName ? 1 : 0;
      } else {
        return aName > bName ? -1 : aName < bName ? 1 : 0;
      }
    });
    this.filteredImagesDTO = [...this.imagesDTO];
  }

  ngOnDestroy() {
    if (this.imageListAutoReloadSub) {
      this.imageListAutoReloadSub.unsubscribe();
    }
  }
}
